import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import {
  SIDEBAR_VIEWS,
  VIEW_ANALYTICS_NAME,
  type SidebarViewId,
} from "../../lib/sidebarViews";
import { AnalyticsEvent, track } from "../../lib/analytics";
import { useUIStore } from "../../store/ui";
import { NavItem, SectionLabel } from "./Sidebar";
import { Icon, type IconId } from "@/components/ui/Icon";

interface SidebarViewsSectionProps {
  /** The VIEWS label + Edit row (phones show Edit in the drawer title instead). */
  showHeader?: boolean;
  onLinkClick?: () => void;
}

/**
 * Map sidebar view ids to translation keys for their labels. The
 * `SIDEBAR_VIEWS` table in `lib/sidebarViews.ts` carries the source-of-truth
 * id + icon, but its `label` field is the English fallback used only by
 * code paths that haven't been translated. UI rendering goes through this
 * map.
 */
export const VIEW_LABEL_KEYS: Record<SidebarViewId, string> = {
  all: "views.all",
  today: "views.today",
  todo: "views.todo",
  pinned: "views.pinned",
  untagged: "views.untagged",
  thisWeek: "views.thisWeek",
  shared: "views.shared",
  sharedWithMe: "views.sharedWithMe",
  attachments: "views.attachments",
  archive: "views.archive",
  trash: "views.trash",
};

export function SidebarViewsSection({ onLinkClick, showHeader = true }: SidebarViewsSectionProps) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { id: noteId } = useParams<{ id?: string }>();

  const sidebarViewOrder = useUIStore((s) => s.sidebarViewOrder);
  const sidebarViewHidden = useUIStore((s) => s.sidebarViewHidden);
  const sidebarEditMode = useUIStore((s) => s.sidebarEditMode);
  const setSidebarEditMode = useUIStore((s) => s.setSidebarEditMode);
  const moveView = useUIStore((s) => s.moveView);
  const toggleViewHidden = useUIStore((s) => s.toggleViewHidden);

  const hiddenSet = useMemo(() => new Set(sidebarViewHidden), [sidebarViewHidden]);

  // Build a sidebar href that preserves the current note path (mirrors Sidebar.tsx)
  const basePath = noteId ? `/app/note/${noteId}` : "/app";
  const buildHref = (params: string | null) =>
    params ? `${basePath}?${params}` : basePath;

  const currentView = searchParams.get("view");
  const currentTag = searchParams.get("tag");
  const isAppPath =
    location.pathname === "/app" || location.pathname.startsWith("/app/note/");

  const isViewActive = (id: SidebarViewId): boolean => {
    if (!isAppPath) return false;
    if (id === "all") return !currentView && !currentTag;
    if (id === "trash") return currentView === "trash";
    if (id === "archive") return currentView === "archive";
    return currentView === id && !currentTag;
  };

  // Exit edit mode on Escape
  useEffect(() => {
    if (!sidebarEditMode) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setSidebarEditMode(false);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [sidebarEditMode, setSidebarEditMode]);

  const sensors = useSensors(
    // Require a small drag threshold so a simple click on the Eye toggle
    // doesn't initiate a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const from = sidebarViewOrder.indexOf(active.id as SidebarViewId);
      const to = sidebarViewOrder.indexOf(over.id as SidebarViewId);
      if (from === -1 || to === -1) return;
      moveView(from, to);
    },
    [moveView, sidebarViewOrder],
  );

  const toggleEditMode = () => setSidebarEditMode(!sidebarEditMode);

  return (
    <div>
      {showHeader && (
      <div className="flex items-center justify-between pr-1 mb-1">
        <SectionLabel>{t("sidebar.views")}</SectionLabel>
        <button
          type="button"
          onClick={toggleEditMode}
          className={`text-[11px] font-semibold uppercase tracking-[0.11em] ${sidebarEditMode ? "text-accent" : "text-ink-secondary"} hover:text-ink bg-transparent border-none cursor-pointer px-1.5 py-0.5 rounded-full -mt-1`}
          aria-label={sidebarEditMode ? t("sidebar.doneEditingViews") : t("sidebar.editViews")}
        >
          {sidebarEditMode ? t("sidebar.done") : t("sidebar.edit")}
        </button>
      </div>
      )}
      {sidebarEditMode ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={sidebarViewOrder}
            strategy={verticalListSortingStrategy}
          >
            <div className="flex flex-col">
              {sidebarViewOrder.map((id) => {
                const def = SIDEBAR_VIEWS[id];
                if (!def) return null;
                return (
                  <EditableViewRow
                    key={id}
                    id={id}
                    label={t(VIEW_LABEL_KEYS[id])}
                    icon={def.icon}
                    hidden={hiddenSet.has(id)}
                    onToggleHidden={() => toggleViewHidden(id)}
                  />
                );
              })}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="flex flex-col">
          {sidebarViewOrder
            .filter((id) => !hiddenSet.has(id))
            .map((id) => {
              const def = SIDEBAR_VIEWS[id];
              if (!def) return null;
              return (
                <NavItem
                  key={id}
                  label={t(VIEW_LABEL_KEYS[id])}
                  icon={<Icon name={def.icon} size={15} />}
                  href={buildHref(def.queryString)}
                  onClick={() => {
                    track(AnalyticsEvent.SmartViewSelected, {
                      view: VIEW_ANALYTICS_NAME[id],
                      source: "sidebar",
                    });
                    onLinkClick?.();
                  }}
                  active={isViewActive(id)}
                />
              );
            })}
        </div>
      )}
    </div>
  );
}

/* ── Editable row ────────────────────────────────────────── */

interface EditableViewRowProps {
  id: SidebarViewId;
  label: string;
  icon: IconId;
  hidden: boolean;
  onToggleHidden: () => void;
}

function EditableViewRow({
  id,
  label,
  icon,
  hidden,
  onToggleHidden,
}: EditableViewRowProps) {
  const { t } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
    opacity: isDragging ? 0.85 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={[
        "flex w-full h-9 items-center gap-2 px-1 rounded-full",
        "text-sm font-normal tracking-tight",
        "bg-transparent text-ink-secondary hover:bg-hover",
        hidden ? "opacity-60" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="flex items-center justify-center h-7 w-5 cursor-grab active:cursor-grabbing bg-transparent border-none p-0 text-muted touch-none"
        aria-label={t("sidebar.reorder", { label })}
      >
        <Icon name="grip" size={14} />
      </button>
      <span className="flex-shrink-0 flex items-center text-muted">
        <Icon name={icon} size={15} />
      </span>
      <span className="truncate flex-1">{label}</span>
      <button
        type="button"
        onClick={onToggleHidden}
        className="h-7 w-7 flex items-center justify-center rounded-full text-muted hover:bg-hover hover:text-ink cursor-pointer bg-transparent border-none p-0 -mr-0.5"
        aria-label={hidden ? t("sidebar.showView", { label }) : t("sidebar.hideView", { label })}
        aria-pressed={!hidden}
      >
        {hidden ? <Icon name="eye-off" size={14} /> : <Icon name="eye" size={14} />}
      </button>
    </div>
  );
}
